<template>
  <teleport to="body">
    <div class="quick-input-overlay" @click="emitClose">
      <div class="quick-input-card" @click.stop>
        <div class="quick-input-header">
          <span class="quick-input-title"> {{ modeLabel }}入力 </span>
          <button type="button" class="quick-input-close" @click="emitClose">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <input
          ref="titleRef"
          v-model="title"
          type="text"
          class="quick-input-field"
          :placeholder="titlePlaceholder"
          @keydown.enter.prevent="submit"
          @keydown.esc.prevent="emitClose"
        />
        <input
          ref="datetimeRef"
          v-model="datetime"
          type="text"
          class="quick-input-field"
          :placeholder="datetimePlaceholder"
          @keydown.enter.prevent="submit"
          @keydown.esc.prevent="emitClose"
        />
        <p class="quick-input-hint">Enterで保存、Escで閉じる</p>
        <p v-if="errorMessage" class="quick-input-error">
          {{ errorMessage }}
        </p>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";

type Mode = "note" | "task" | "schedule";

interface Props {
  mode: Mode;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  close: [];
  submitted: [mode: Mode];
}>();

const title = ref("");
const datetime = ref("");
const errorMessage = ref("");
const isSubmitting = ref(false);
const titleRef = ref<HTMLInputElement | null>(null);
const datetimeRef = ref<HTMLInputElement | null>(null);

const modeLabel = computed(() => {
  if (props.mode === "task") return "タスク";
  if (props.mode === "schedule") return "スケジュール";
  return "メモ";
});

const titlePlaceholder = computed(() => {
  if (props.mode === "task") return "例: 図書館で本を返す";
  if (props.mode === "schedule") return "例: チームMTG";
  return "例: 研究室に連絡";
});

const datetimePlaceholder = computed(() => {
  if (props.mode === "task") return "期限（例: 明日15時）";
  if (props.mode === "schedule") return "日時（例: 明日15時）";
  return "日付や時間（任意）";
});

const focusInput = (): void => {
  if (titleRef.value) {
    titleRef.value.focus();
    titleRef.value.select();
  }
};

const emitClose = (): void => {
  emit("close");
};

const submit = async (): Promise<void> => {
  if (isSubmitting.value) return;
  const titleValue = title.value.trim();
  const datetimeValue = datetime.value.trim();
  if (!titleValue) return;

  isSubmitting.value = true;
  errorMessage.value = "";

  try {
    const response = await fetch("/api/quick-entry", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        mode: props.mode,
        title: titleValue,
        datetime: datetimeValue || null,
      }),
    });

    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || "保存に失敗しました");
    }

    emit("submitted", props.mode);
    emitClose();
  } catch (error) {
    errorMessage.value =
      error instanceof Error ? error.message : "保存に失敗しました";
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  nextTick(() => {
    focusInput();
  });
});
</script>

<style scoped>
.quick-input-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 10, 10, 0.25);
  z-index: 400;
  padding: 16px;
}

.quick-input-card {
  width: min(520px, 92vw);
  background: white;
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.quick-input-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.quick-input-title {
  font-size: 14px;
  font-weight: 600;
  color: #222;
}

.quick-input-close {
  border: none;
  background: transparent;
  color: #666;
  cursor: pointer;
}

.quick-input-field {
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 14px;
  outline: none;
}

.quick-input-field:focus {
  border-color: #1f73e6;
  box-shadow: 0 0 0 3px rgba(31, 115, 230, 0.15);
}

.quick-input-hint {
  margin: 0;
  font-size: 11px;
  color: #777;
}

.quick-input-error {
  margin: 0;
  font-size: 12px;
  color: #d33b27;
}
</style>
